/**
 * Procedural 16-Bit Arcade Audio & BGM Synthesizer using Web Audio API.
 * 
 * 100% Programmatic, Royalty-Free, Zero External Assets, Zero Network Latency.
 * 100% Legal, Open Source & Allowed for Commercial Use.
 */

let audioCtx: AudioContext | null = null;
let sfxEnabled = true;
let bgmEnabled = true;
let bgmPlaying = false;
let bgmLoopTimer: number | null = null;
let currentBgmStep = 0;

function getContext(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (!audioCtx) {
    const AudioContextClass =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext })
        .webkitAudioContext;
    if (AudioContextClass) {
      audioCtx = new AudioContextClass();
    }
  }
  if (audioCtx && audioCtx.state === "suspended") {
    audioCtx.resume();
  }
  return audioCtx;
}

export function toggleSound(): boolean {
  sfxEnabled = !sfxEnabled;
  if (sfxEnabled) {
    playSound("parry");
  }
  return sfxEnabled;
}

export function toggleBgm(): boolean {
  bgmEnabled = !bgmEnabled;
  if (!bgmEnabled) {
    stopBgm();
  } else {
    startBgm();
  }
  return bgmEnabled;
}

export function isSoundEnabled(): boolean {
  return sfxEnabled;
}

export function isBgmEnabled(): boolean {
  return bgmEnabled;
}

// -----------------------------------------------------------
// PROCEDURAL 16-BIT RETRO FIGHTING GAME BGM SEQUENCER
// -----------------------------------------------------------
// High-energy D-Minor / Pentatonic Martial Arts Arcade Battle Theme (132 BPM)
const BPM = 132;
const STEP_TIME = 60 / BPM / 4; // 16th note step in seconds (~0.1136s)

// Frequency table for melody notes
const N = {
  REST: 0,
  D2: 73.42,
  F2: 87.31,
  G2: 98.0,
  A2: 110.0,
  C3: 130.81,
  D3: 146.83,
  E3: 164.81,
  F3: 174.61,
  G3: 196.0,
  A3: 220.0,
  C4: 261.63,
  D4: 293.66,
  E4: 329.63,
  F4: 349.23,
  G4: 392.0,
  A4: 440.0,
  C5: 523.25,
  D5: 587.33,
  F5: 698.46,
};

// 32-step battle groove pattern
const BASS_PATTERN = [
  N.D2, N.D2, N.D3, N.D2, N.F2, N.D2, N.G2, N.A2,
  N.D2, N.D2, N.D3, N.D2, N.C3, N.D2, N.F2, N.G2,
  N.D2, N.D2, N.D3, N.D2, N.F2, N.D2, N.G2, N.A2,
  N.A2, N.G2, N.F2, N.G2, N.A2, N.C3, N.D3, N.REST,
];

const MELODY_PATTERN = [
  N.D4, N.REST, N.F4, N.G4, N.A4, N.REST, N.D5, N.REST,
  N.C5, N.A4, N.REST, N.G4, N.F4, N.G4, N.A4, N.REST,
  N.D4, N.REST, N.F4, N.G4, N.A4, N.C5, N.D5, N.F5,
  N.D5, N.C5, N.A4, N.G4, N.F4, N.D4, N.REST, N.REST,
];

const DRUM_PATTERN = [
  // 1 = Kick, 2 = Snare, 3 = Hat, 4 = Kick+Hat, 5 = Snare+Hat, 0 = Rest
  4, 3, 3, 3, 5, 3, 4, 3,
  4, 3, 4, 3, 5, 3, 3, 4,
  4, 3, 3, 3, 5, 3, 4, 3,
  5, 3, 4, 5, 5, 4, 5, 2,
];

function playDrumKick(ctx: AudioContext, time: number) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "sine";
  osc.frequency.setValueAtTime(140, time);
  osc.frequency.exponentialRampToValueAtTime(32, time + 0.08);

  gain.gain.setValueAtTime(0.35, time);
  gain.gain.exponentialRampToValueAtTime(0.001, time + 0.09);

  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(time);
  osc.stop(time + 0.09);
}

function playDrumSnare(ctx: AudioContext, time: number) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "triangle";
  osc.frequency.setValueAtTime(220, time);
  osc.frequency.exponentialRampToValueAtTime(60, time + 0.06);

  gain.gain.setValueAtTime(0.22, time);
  gain.gain.exponentialRampToValueAtTime(0.001, time + 0.07);

  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(time);
  osc.stop(time + 0.07);
}

function playDrumHat(ctx: AudioContext, time: number) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  const filter = ctx.createBiquadFilter();

  osc.type = "square";
  osc.frequency.setValueAtTime(8000, time);

  filter.type = "highpass";
  filter.frequency.setValueAtTime(7000, time);

  gain.gain.setValueAtTime(0.06, time);
  gain.gain.exponentialRampToValueAtTime(0.001, time + 0.03);

  osc.connect(filter);
  filter.connect(gain);
  gain.connect(ctx.destination);
  osc.start(time);
  osc.stop(time + 0.03);
}

function playBassNote(ctx: AudioContext, freq: number, time: number) {
  if (freq === 0) return;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "sawtooth";
  osc.frequency.setValueAtTime(freq, time);

  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.setValueAtTime(450, time);

  gain.gain.setValueAtTime(0.22, time);
  gain.gain.exponentialRampToValueAtTime(0.001, time + STEP_TIME * 0.9);

  osc.connect(filter);
  filter.connect(gain);
  gain.connect(ctx.destination);
  osc.start(time);
  osc.stop(time + STEP_TIME * 0.9);
}

function playMelodyNote(ctx: AudioContext, freq: number, time: number) {
  if (freq === 0) return;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  const filter = ctx.createBiquadFilter();

  osc.type = "square";
  osc.frequency.setValueAtTime(freq, time);

  filter.type = "lowpass";
  filter.frequency.setValueAtTime(2400, time);
  filter.frequency.exponentialRampToValueAtTime(600, time + STEP_TIME * 1.4);

  gain.gain.setValueAtTime(0.13, time);
  gain.gain.exponentialRampToValueAtTime(0.001, time + STEP_TIME * 1.3);

  osc.connect(filter);
  filter.connect(gain);
  gain.connect(ctx.destination);
  osc.start(time);
  osc.stop(time + STEP_TIME * 1.4);
}

export function startBgm() {
  if (!bgmEnabled || bgmPlaying) return;
  const ctx = getContext();
  if (!ctx) return;

  bgmPlaying = true;
  currentBgmStep = 0;

  const scheduleStep = () => {
    if (!bgmPlaying || !bgmEnabled) return;
    const now = ctx.currentTime;
    const stepIdx = currentBgmStep % 32;

    // 1. Bass
    const bassFreq = BASS_PATTERN[stepIdx];
    playBassNote(ctx, bassFreq, now);

    // 2. Melody
    const melFreq = MELODY_PATTERN[stepIdx];
    playMelodyNote(ctx, melFreq, now);

    // 3. Drums
    const drum = DRUM_PATTERN[stepIdx];
    if (drum === 1 || drum === 4) playDrumKick(ctx, now);
    if (drum === 2 || drum === 5) playDrumSnare(ctx, now);
    if (drum === 3 || drum === 4 || drum === 5) playDrumHat(ctx, now);

    currentBgmStep++;
    bgmLoopTimer = window.setTimeout(scheduleStep, STEP_TIME * 1000);
  };

  scheduleStep();
}

export function stopBgm() {
  bgmPlaying = false;
  if (bgmLoopTimer) {
    clearTimeout(bgmLoopTimer);
    bgmLoopTimer = null;
  }
}

// -----------------------------------------------------------
// COMPREHENSIVE 16-BIT SOUND EFFECTS (SFX)
// -----------------------------------------------------------

export type SfxType =
  | "slash"
  | "punch"
  | "heavy"
  | "parry"
  | "block"
  | "dash"
  | "special"
  | "gong"
  | "ko"
  | "victory"
  | "double_jump"
  | "sweep"
  | "launcher"
  | "ground_slam"
  | "guard_break"
  | "counter_hit"
  | "combo_hit";

export function playSound(type: SfxType, pitchModifier = 1.0) {
  if (!sfxEnabled) return;
  const ctx = getContext();
  if (!ctx) return;

  const now = ctx.currentTime;

  try {
    switch (type) {
      case "double_jump": {
        // Ninja flip whoosh with chime
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(440, now);
        osc.frequency.exponentialRampToValueAtTime(1100, now + 0.12);

        gain.gain.setValueAtTime(0.24, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.12);

        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now);
        osc.stop(now + 0.12);
        break;
      }

      case "sweep": {
        // Low spinning leg sweep whoosh
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "triangle";
        osc.frequency.setValueAtTime(180, now);
        osc.frequency.exponentialRampToValueAtTime(45, now + 0.14);

        gain.gain.setValueAtTime(0.32, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.14);

        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now);
        osc.stop(now + 0.14);
        break;
      }

      case "launcher": {
        // Rising dragon uppercut / rising blade sound
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sawtooth";
        osc.frequency.setValueAtTime(220, now);
        osc.frequency.exponentialRampToValueAtTime(950, now + 0.22);

        gain.gain.setValueAtTime(0.28, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.22);

        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now);
        osc.stop(now + 0.22);
        break;
      }

      case "ground_slam": {
        // Heavy downward floor impact
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "triangle";
        osc.frequency.setValueAtTime(160, now);
        osc.frequency.exponentialRampToValueAtTime(28, now + 0.26);

        gain.gain.setValueAtTime(0.42, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.26);

        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now);
        osc.stop(now + 0.26);
        break;
      }

      case "guard_break": {
        // Piercing shield shatter
        const freqs = [350, 720, 1480, 2200];
        freqs.forEach((f) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = "square";
          osc.frequency.setValueAtTime(f, now);
          osc.frequency.exponentialRampToValueAtTime(f * 0.4, now + 0.25);

          gain.gain.setValueAtTime(0.18, now);
          gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);

          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.start(now);
          osc.stop(now + 0.25);
        });
        break;
      }

      case "counter_hit": {
        // Heavy gong chime on counter hit
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(1240, now);
        osc.frequency.exponentialRampToValueAtTime(310, now + 0.35);

        gain.gain.setValueAtTime(0.35, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.35);

        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now);
        osc.stop(now + 0.35);
        break;
      }

      case "combo_hit": {
        // Ascending pitch hit for combo chains
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sawtooth";
        const base = 300 * Math.min(2.5, pitchModifier);
        osc.frequency.setValueAtTime(base, now);
        osc.frequency.exponentialRampToValueAtTime(base * 0.6, now + 0.1);

        gain.gain.setValueAtTime(0.25, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.1);

        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now);
        osc.stop(now + 0.1);
        break;
      }

      case "slash": {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        const filter = ctx.createBiquadFilter();

        osc.type = "sawtooth";
        osc.frequency.setValueAtTime(800, now);
        osc.frequency.exponentialRampToValueAtTime(140, now + 0.14);

        filter.type = "bandpass";
        filter.frequency.setValueAtTime(1800, now);
        filter.Q.setValueAtTime(3, now);

        gain.gain.setValueAtTime(0.28, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.14);

        osc.connect(filter);
        filter.connect(gain);
        gain.connect(ctx.destination);

        osc.start(now);
        osc.stop(now + 0.14);
        break;
      }

      case "punch": {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "triangle";
        osc.frequency.setValueAtTime(240, now);
        osc.frequency.exponentialRampToValueAtTime(40, now + 0.12);

        gain.gain.setValueAtTime(0.35, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.12);

        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.start(now);
        osc.stop(now + 0.12);
        break;
      }

      case "heavy": {
        const osc = ctx.createOscillator();
        const osc2 = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = "sawtooth";
        osc.frequency.setValueAtTime(160, now);
        osc.frequency.exponentialRampToValueAtTime(30, now + 0.28);

        osc2.type = "sine";
        osc2.frequency.setValueAtTime(90, now);
        osc2.frequency.exponentialRampToValueAtTime(25, now + 0.35);

        gain.gain.setValueAtTime(0.45, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.35);

        osc.connect(gain);
        osc2.connect(gain);
        gain.connect(ctx.destination);

        osc.start(now);
        osc2.start(now);
        osc.stop(now + 0.35);
        osc2.stop(now + 0.35);
        break;
      }

      case "parry": {
        const osc1 = ctx.createOscillator();
        const osc2 = ctx.createOscillator();
        const gain = ctx.createGain();

        osc1.type = "sine";
        osc1.frequency.setValueAtTime(1760, now);
        osc1.frequency.exponentialRampToValueAtTime(880, now + 0.22);

        osc2.type = "triangle";
        osc2.frequency.setValueAtTime(3520, now);
        osc2.frequency.exponentialRampToValueAtTime(1760, now + 0.2);

        gain.gain.setValueAtTime(0.32, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);

        osc1.connect(gain);
        osc2.connect(gain);
        gain.connect(ctx.destination);

        osc1.start(now);
        osc2.start(now);
        osc1.stop(now + 0.25);
        osc2.stop(now + 0.25);
        break;
      }

      case "block": {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "square";
        osc.frequency.setValueAtTime(110, now);
        osc.frequency.exponentialRampToValueAtTime(45, now + 0.09);

        gain.gain.setValueAtTime(0.2, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.09);

        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.start(now);
        osc.stop(now + 0.09);
        break;
      }

      case "dash": {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        const filter = ctx.createBiquadFilter();

        osc.type = "triangle";
        osc.frequency.setValueAtTime(320, now);
        osc.frequency.linearRampToValueAtTime(180, now + 0.12);

        filter.type = "lowpass";
        filter.frequency.setValueAtTime(800, now);

        gain.gain.setValueAtTime(0.22, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.12);

        osc.connect(filter);
        filter.connect(gain);
        gain.connect(ctx.destination);

        osc.start(now);
        osc.stop(now + 0.12);
        break;
      }

      case "special": {
        const osc = ctx.createOscillator();
        const osc2 = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = "sawtooth";
        osc.frequency.setValueAtTime(220, now);
        osc.frequency.exponentialRampToValueAtTime(880, now + 0.35);

        osc2.type = "square";
        osc2.frequency.setValueAtTime(440, now);
        osc2.frequency.exponentialRampToValueAtTime(1320, now + 0.35);

        gain.gain.setValueAtTime(0.35, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.45);

        osc.connect(gain);
        osc2.connect(gain);
        gain.connect(ctx.destination);

        osc.start(now);
        osc2.start(now);
        osc.stop(now + 0.45);
        osc2.stop(now + 0.45);
        break;
      }

      case "gong": {
        const freqs = [196, 293.66, 392, 587.33];
        freqs.forEach((f, i) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = "sine";
          osc.frequency.setValueAtTime(f, now);

          const vol = 0.25 / (i + 1);
          gain.gain.setValueAtTime(vol, now);
          gain.gain.exponentialRampToValueAtTime(0.001, now + 1.2);

          osc.connect(gain);
          gain.connect(ctx.destination);

          osc.start(now);
          osc.stop(now + 1.2);
        });
        break;
      }

      case "ko": {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(140, now);
        osc.frequency.exponentialRampToValueAtTime(20, now + 0.8);

        gain.gain.setValueAtTime(0.6, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.8);

        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.start(now);
        osc.stop(now + 0.8);
        break;
      }

      case "victory": {
        const notes = [392, 493.88, 587.33, 783.99];
        notes.forEach((freq, idx) => {
          const noteStart = now + idx * 0.12;
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = "square";
          osc.frequency.setValueAtTime(freq, noteStart);

          const dur = idx === 3 ? 0.5 : 0.11;
          gain.gain.setValueAtTime(0.2, noteStart);
          gain.gain.exponentialRampToValueAtTime(0.001, noteStart + dur);

          osc.connect(gain);
          gain.connect(ctx.destination);

          osc.start(noteStart);
          osc.stop(noteStart + dur);
        });
        break;
      }
    }
  } catch (err) {
    console.debug("Audio error:", err);
  }
}
